import { parseMoneyToCents, sumCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The shift handovers a franchise has on record.
 *
 * `manager-orders` owns the screen that *creates* one; this is the screen that
 * reviews them. The two share no arithmetic — creating a handover computes what is
 * owed from a live wallet, reviewing one checks whether what was recorded holds
 * together — so the logic stays separate rather than being forced into one module.
 *
 * Ported from legacy `pages/revenue-handovers.tsx`, which shows the stored numbers
 * and never compares them. FB-068 is what that misses.
 */

export interface HandoverReport {
  id?: number
  employeeId?: number
  employeeName?: string
  totalRevenue?: MoneyInput
  /** The online card figure the person entered. */
  cardPayAmount?: MoneyInput
  /** The terminal card figure the person entered. */
  cardAmount?: MoneyInput
  /** FB-068's new deduction. `null`/absent means the record predates it — not that nought was taken. */
  cardCourierAmount?: MoneyInput
  vouchersAmount?: MoneyInput
  fuelAmount?: MoneyInput
  purchase1Amount?: MoneyInput
  purchase2Amount?: MoneyInput
  payout1Amount?: MoneyInput
  payout1Note?: string | null
  payout2Amount?: MoneyInput
  payout2Note?: string | null
  cashToHandOver?: MoneyInput
  orderCount?: number
  /** Wallet snapshots — what the system says was taken, at the time. */
  cashTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
  createdAt?: string
}

/** Everything the person said did not become cash in their pocket. */
export function deductionCents(report: HandoverReport): number {
  return sumCents(
    report.cardPayAmount,
    report.cardAmount,
    report.vouchersAmount,
    report.fuelAmount,
    report.purchase1Amount,
    report.purchase2Amount,
    report.payout1Amount,
    report.payout2Amount,
  )
}

export interface HandoverAudit {
  totalRevenueCents: number
  deductionCents: number
  cashToHandOverCents: number
  /** What the record itself says the cash should be. */
  expectedCashCents: number
  /** The record does not add up. */
  arithmeticOff: boolean
  /** What the wallet says was taken by card, terminal + courier. */
  walletCardCents: number
  /** What the person entered as the terminal card figure. */
  enteredCardCents: number
  /** Entered minus wallet. Positive means they deducted more card than was taken. */
  cardDriftCents: number
  cardDrifts: boolean
  /**
   * Whether this record states card taken at the door — FB-068.
   *
   * False for every handover written before the column existed, which is the 363 in the dumps. That
   * is why `walletCardCents` above still means terminal **plus** courier for them: with no statement
   * of which convention was used, the combined figure is the only reading available.
   */
  doorStated: boolean
  /** What the register says was taken at the door. */
  doorWalletCents: number
  /** What the person entered for it — nought when the record does not state it. */
  doorEnteredCents: number
  doorDriftCents: number
  doorDrifts: boolean
}

/** Anything under a cent is rounding, not a discrepancy. */
const TOLERANCE_CENTS = 0

/**
 * Whether a stored handover holds together, and whether it agrees with the wallet.
 *
 * Two separate questions, and only the first one legacy could have answered:
 *
 *   1. **Does the record add up?** Revenue minus the deductions should equal the
 *      cash handed over. Across all 363 handovers in the restored data it always
 *      does, so the form's own arithmetic is sound.
 *   2. **Does the card figure match what was actually taken by card?** In **103 of
 *      those 363** it does not. That is FB-068, and it is the one that costs money:
 *      a card figure entered too high means the person hands over less cash than
 *      they took, and too low means more.
 *
 * The record **used to have no field for courier card takings**, so a driver's card-at-the-door was
 * either folded into `cardAmount` or not deducted at all, and the 103 mismatches say it was not done
 * consistently. `card_courier_amount` exists now (`MIGRATION-STATUS` FB-068), and this compares like
 * with like when it is stated:
 *
 * - **Stated** — the entered terminal figure is checked against the register's terminal, and the
 *   entered door figure against the register's courier. Two comparisons, neither of them a guess.
 * - **Absent** — the historic case, and the 103. The entered `cardAmount` is checked against
 *   terminal **plus** courier, exactly as before, because that is the only reading available for a
 *   record whose convention nobody wrote down. Those stay flagged, which is right: the money really
 *   is unaccounted for and the audit is what says so.
 */
export function auditHandover(report: HandoverReport): HandoverAudit {
  const totalRevenueCents = parseMoneyToCents(report.totalRevenue)
  const deductions = deductionCents(report)
  const cashToHandOverCents = parseMoneyToCents(report.cashToHandOver)
  const expectedCashCents = totalRevenueCents - deductions

  // Stated only when the field carries a value. An empty string and `null` are both *not stated*;
  // `0` is a driver saying they took no card at the door, which is a claim and is treated as one.
  const doorStated = report.cardCourierAmount !== null && report.cardCourierAmount !== undefined
  const walletCardCents = doorStated
    ? parseMoneyToCents(report.cardTerminalTotal)
    : sumCents(report.cardTerminalTotal, report.cardCourierTotal)
  const enteredCardCents = parseMoneyToCents(report.cardAmount)
  const cardDriftCents = enteredCardCents - walletCardCents

  // The second comparison, and it only exists for a record that states the figure.
  const doorWalletCents = parseMoneyToCents(report.cardCourierTotal)
  const doorEnteredCents = doorStated ? parseMoneyToCents(report.cardCourierAmount) : 0
  const doorDriftCents = doorStated ? doorEnteredCents - doorWalletCents : 0

  return {
    totalRevenueCents,
    deductionCents: deductions,
    cashToHandOverCents,
    expectedCashCents,
    arithmeticOff: Math.abs(expectedCashCents - cashToHandOverCents) > TOLERANCE_CENTS,
    walletCardCents,
    enteredCardCents,
    cardDriftCents,
    cardDrifts: Math.abs(cardDriftCents) > TOLERANCE_CENTS,
    doorStated,
    doorWalletCents,
    doorEnteredCents,
    doorDriftCents,
    doorDrifts: doorStated && Math.abs(doorDriftCents) > TOLERANCE_CENTS,
  }
}

export interface HandoverTotals {
  count: number
  revenueCents: number
  cashCents: number
  orderCount: number
  /** The wallet's own split, summed — what the register says was taken, by method. */
  walletCashCents: number
  walletTerminalCents: number
  walletCourierCents: number
  walletOnlineCents: number
  /** How many records disagree with the wallet about card takings. */
  driftingCount: number
  /** The net of those disagreements — what the discrepancies come to. */
  netDriftCents: number
  /**
   * What came out of the cash over the period — §9.252, the owner's item K.
   *
   * `cashToHandOver` is already net of all four, so the headline has always been real cash. These
   * are what it is net *of*, which no tile could answer.
   */
  payoutsCents: number
  purchasesCents: number
  fuelCents: number
  vouchersCents: number
  /** How many records carry a payout note, so an unexplained payout is visible. */
  annotatedPayouts: number
}

export function handoverTotals(reports: HandoverReport[]): HandoverTotals {
  const audits = reports.map(auditHandover)
  const sum = (pick: (report: HandoverReport) => MoneyInput): number =>
    reports.reduce((total, report) => total + parseMoneyToCents(pick(report)), 0)

  return {
    count: reports.length,
    revenueCents: audits.reduce((total, audit) => total + audit.totalRevenueCents, 0),
    cashCents: audits.reduce((total, audit) => total + audit.cashToHandOverCents, 0),
    orderCount: reports.reduce((total, report) => total + (report.orderCount ?? 0), 0),
    walletCashCents: sum((report) => report.cashTotal),
    walletTerminalCents: sum((report) => report.cardTerminalTotal),
    walletCourierCents: sum((report) => report.cardCourierTotal),
    walletOnlineCents: sum((report) => report.cardOnlineTotal),
    driftingCount: audits.filter((audit) => audit.cardDrifts).length,
    netDriftCents: audits.reduce((total, audit) => total + audit.cardDriftCents, 0),
    // §9.252 — what came *out* of the cash over the period. `cashToHandOver` has always been net of
    // these, so the headline was already real cash; what nobody could see was how much of a week
    // went on wages and how much on a purchase without opening every record.
    //
    // CardPay and *Karta* are deliberately not among them: they are the same takings arriving by
    // another route, not money leaving the till, and counting them here would double against the
    // wallet split already on screen.
    payoutsCents: sum((report) => report.payout1Amount) + sum((report) => report.payout2Amount),
    purchasesCents: sum((report) => report.purchase1Amount) + sum((report) => report.purchase2Amount),
    fuelCents: sum((report) => report.fuelAmount),
    vouchersCents: sum((report) => report.vouchersAmount),
    /** How many of the period's payouts carry a note — an unexplained one is worth seeing. */
    annotatedPayouts: reports.filter((report) => payoutNotes(report).length > 0).length,
  }
}

/** The typed lines a handover collapsed into its two payout slots. */
export function payoutNotes(report: HandoverReport): string[] {
  return [report.payout1Note, report.payout2Note].filter(
    (note): note is string => typeof note === 'string' && note.trim() !== '',
  )
}

export function matchesSearch(report: HandoverReport, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [report.employeeName, ...payoutNotes(report)]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle))
}

export type HandoverFilter = 'all' | 'drifting'

export function filterHandovers(
  reports: HandoverReport[],
  { filter, search }: { filter: HandoverFilter; search: string },
): HandoverReport[] {
  return reports.filter((report) => {
    if (filter === 'drifting' && !auditHandover(report).cardDrifts) return false
    return matchesSearch(report, search)
  })
}

/** The label for a drift, phrased so its direction is unambiguous. */
export function driftDirection(cardDriftCents: number): string {
  if (cardDriftCents > 0) return tt('odpočítal viac kariet, než kasa zaznamenala')
  return tt('odpočítal menej kariet, než kasa zaznamenala')
}
